import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useUndoDelete } from "@/lib/useUndoDelete";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ADMIN } from "@/constants/testIds";
import { Upload, Trash2, Pencil, Download, PackageOpen, Zap, EyeOff, Loader2, FlaskConical } from "lucide-react";
import BlogEditor from "@/components/BlogEditor";
import DropZone from "@/components/DropZone";
import ListFilter from "@/components/admin/ListFilter";

export default function UploadPanel({ blogs, refresh }) {
  const [file, setFile] = useState(null);
  const [authorId, setAuthorId] = useState("none");
  const [releaseDate, setReleaseDate] = useState("");
  const [authors, setAuthors] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [zipUploading, setZipUploading] = useState(false);
  const [zipReport, setZipReport] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => { api.get("/authors").then(r => setAuthors(r.data)); }, []);

  // POST the file to /api/blogs/preview-docx so admins see what will be
  // extracted before committing. Runs every time a new .docx is staged.
  const runPreview = async (docxFile) => {
    if (!docxFile) { setPreview(null); return; }
    setPreviewing(true);
    setPreview(null);
    const fd = new FormData();
    fd.append("file", docxFile);
    const backend = process.env.REACT_APP_BACKEND_URL;
    try {
      const resp = await fetch(`${backend}/api/blogs/preview-docx`, { method: "POST", body: fd, credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setPreview(data);
        // Auto-select the fuzzy-matched author record so admin doesn't have to
        // pick manually when the docx byline matches an existing author.
        if (data.suggested_author_id) {
          setAuthorId(data.suggested_author_id);
        }
      } else {
        setPreview({ __error: `Preview failed (HTTP ${resp.status})` });
      }
    } catch (e) {
      setPreview({ __error: "Preview request failed — check your connection" });
    } finally {
      setPreviewing(false);
    }
  };

  const stageFile = (f) => {
    setFile(f);
    if (f) runPreview(f); else setPreview(null);
  };

  // Bulk import from a .zip of .docx files. Uses the same author /
  // release date currently set in the form so admins can queue up 20 files
  // and stamp them with the same metadata in one shot.
  const uploadZip = async (zipFile) => {
    if (!zipFile) return;
    if (!/\.zip$/i.test(zipFile.name)) return toast.error("Only .zip files are supported");
    setZipUploading(true);
    setZipReport(null);
    const fd = new FormData();
    fd.append("file", zipFile);
    if (authorId && authorId !== "none") fd.append("author_id", authorId);
    if (releaseDate) fd.append("release_date", releaseDate);
    const backend = process.env.REACT_APP_BACKEND_URL;
    let resp;
    try {
      resp = await fetch(`${backend}/api/blogs/upload-zip`, { method: "POST", body: fd, credentials: "include" });
    } catch (err) {
      toast.error("Network error — could not reach the server.");
      setZipUploading(false);
      return;
    }
    if (!resp.ok) {
      let detail = "";
      try { detail = (await resp.json()).detail || ""; } catch {}
      toast.error(detail || `Zip import failed (HTTP ${resp.status})`);
      setZipUploading(false);
      return;
    }
    const data = await resp.json();
    setZipReport(data);
    if (data.created) toast.success(`Imported ${data.created} blog${data.created === 1 ? "" : "s"} from zip`);
    if (data.skipped) toast.message(`${data.skipped} skipped (already exist)`);
    if (data.errors) toast.error(`${data.errors} failed — see report below`);
    refresh();
    setZipUploading(false);
  };

  // Core upload — separated so the form submit and the drop-zone both hit
  // the same code path.
  const uploadDocx = async (docxFile) => {
    if (!docxFile) return toast.error("Choose a .docx");
    if (!/\.docx$/i.test(docxFile.name)) return toast.error("Only .docx files are supported");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", docxFile);
    if (authorId && authorId !== "none") fd.append("author_id", authorId);
    if (releaseDate) fd.append("release_date", releaseDate);
    const backend = process.env.REACT_APP_BACKEND_URL;
    let resp;
    try {
      resp = await fetch(`${backend}/api/blogs/upload`, { method: "POST", body: fd, credentials: "include" });
    } catch (err) {
      console.error("Upload fetch error:", err);
      toast.error("Network error — could not reach the server.");
      setUploading(false);
      return;
    }
    if (resp.ok) {
      toast.success("Blog uploaded and formatted");
      setFile(null); setReleaseDate(""); setPreview(null);
      const el = document.getElementById("upload-file-input"); if (el) el.value = "";
      refresh();
      setUploading(false);
      return;
    }
    let msg;
    if (resp.status === 401) msg = "Not signed in — please sign in as admin and try again.";
    else if (resp.status === 403) msg = "Admin access required to upload blogs.";
    else if (resp.status === 413) msg = "File too large.";
    else {
      let detail = "";
      try { const j = await resp.json(); detail = j.detail || ""; }
      catch { try { detail = (await resp.text()).slice(0, 200); } catch { detail = ""; } }
      msg = detail ? String(detail) : `Upload failed (HTTP ${resp.status})`;
    }
    toast.error(msg);
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    await uploadDocx(file);
  };
  const undoDelete = useUndoDelete();
  const [blogFilter, setBlogFilter] = useState("");
  // Case-insensitive title / blog_id filter. Empty filter shows everything.
  const filteredBlogs = React.useMemo(() => {
    const q = blogFilter.trim().toLowerCase();
    if (!q) return blogs;
    return blogs.filter(b =>
      (b.title || "").toLowerCase().includes(q) ||
      (b.blog_id || "").toLowerCase().includes(q)
    );
  }, [blogs, blogFilter]);
  const del = (b) => undoDelete.run({
    label: `Deleted "${b.title}"`,
    deletePath: `/blogs/${b.blog_id}`,
    restorePath: `/blogs/${b.blog_id}/restore`,
    onDone: refresh,
  });

  // One-click publish — stamps `release_date` with today's date so the blog
  // immediately shows up for public readers. Only offered on drafts and
  // scheduled (future-dated) posts.
  const publishNow = async (blog) => {
    const today = new Date().toISOString().slice(0, 10);
    const label = blog.release_date ? `scheduled for ${blog.release_date}` : "this draft";
    if (!confirm(`Publish ${blog.title} today (${today})?\n\nThis will make ${label} visible to all readers immediately.`)) return;
    try {
      await api.put(`/blogs/${blog.blog_id}`, { release_date: today });
      toast.success(`Published — visible to readers as of ${today}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not publish");
    }
  };

  // Reverse of Publish Now — clears `release_date` so the blog drops back to
  // draft and disappears from the public site. Only offered on live posts.
  const unpublish = async (blog) => {
    if (!confirm(`Unpublish "${blog.title}"?\n\nIt will be hidden from readers and returned to Draft. All content is preserved.`)) return;
    try {
      await api.put(`/blogs/${blog.blog_id}`, { release_date: null });
      toast.success("Unpublished — blog is now a draft");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unpublish");
    }
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800 mt-6">
      <CardHeader><CardTitle className="font-heading">Upload blog (.docx)</CardTitle></CardHeader>
      <CardContent>
        <DropZone
          testId="upload-blog-dropzone"
          accept={(f) => /\.docx$/i.test(f.name) || /\.zip$/i.test(f.name) || f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || f.type === "application/zip"}
          label="Drop .docx to attach — or drop a .zip of .docx files to bulk-import"
          onFiles={(files) => {
            const f = files[0];
            if (!f) return;
            if (/\.zip$/i.test(f.name)) {
              uploadZip(f);
              return;
            }
            // Stage a single .docx into the form.
            stageFile(f);
            const el = document.getElementById("upload-file-input");
            if (el) {
              try {
                const dt = new DataTransfer();
                dt.items.add(f);
                el.files = dt.files;
              } catch { /* Some browsers block programmatic assignment — form state still has it. */ }
            }
            toast.success(`${f.name} attached — review the preview below, then click Upload and format`);
          }}
          className="rounded-md"
        >
        <form data-testid={ADMIN.uploadForm} onSubmit={submit} className="grid md:grid-cols-6 gap-3 items-end p-1">
          <div className="md:col-span-6"><label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Word document</label>
            <input data-testid={ADMIN.uploadFile} id="upload-file-input" type="file" accept=".docx"
              onChange={e => stageFile(e.target.files[0])}
              className="block w-full text-sm border border-slate-300 dark:border-slate-700 rounded-md p-2"/></div>
          <div className="md:col-span-3"><label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Author</label>
            <Select value={authorId} onValueChange={setAuthorId}>
              <SelectTrigger data-testid="admin-upload-author"><SelectValue placeholder="— None —"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {authors.map(a => <SelectItem key={a.author_id} value={a.author_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Add authors in the Users tab section below or ask an admin.</p>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Release date <span className="text-slate-400 dark:text-slate-500 normal-case">(publishes on this day, leave blank = draft)</span>
            </label>
            <Input data-testid="admin-upload-release-date" type="date" value={releaseDate} onChange={e=>setReleaseDate(e.target.value)}/>
          </div>
          <div className="md:col-span-6 flex flex-wrap items-center gap-3">
            <Button data-testid={ADMIN.uploadSubmit} type="submit" disabled={uploading} className="bg-sky-700 hover:bg-sky-600">
              <Upload className="w-4 h-4 mr-2"/>{uploading ? "Uploading…" : "Upload and format"}
            </Button>
            <label className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded-md border cursor-pointer ${zipUploading ? "border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 cursor-wait" : "border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-sky-500 hover:text-sky-700"}`}
                   data-testid="admin-upload-zip-label" title="Bulk-create blogs from a .zip of .docx files">
              <PackageOpen className="w-4 h-4"/>{zipUploading ? "Importing zip…" : "Bulk import (.zip)"}
              <input type="file" accept=".zip,application/zip" className="sr-only"
                     data-testid="admin-upload-zip-input"
                     disabled={zipUploading}
                     onChange={(e)=>{ const f=e.target.files?.[0]; if(f){ uploadZip(f); e.target.value=""; } }}/>
            </label>
            <p className="basis-full text-xs text-slate-400 dark:text-slate-500 mt-1">Tip — you can also <strong>drag a .docx or .zip anywhere onto this card</strong>. A single .docx attaches to the form for detail entry; a .zip triggers a bulk import using the Author / Release date currently set above. Existing blogs (matched by slug) are skipped.</p>
          </div>
        </form>
        </DropZone>
        {zipReport && (
          <div data-testid="admin-upload-zip-report" className="mt-4 border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-slate-600 dark:text-slate-300">
                Zip import — <span className="text-emerald-700 dark:text-emerald-300">{zipReport.created} created</span>
                {" · "}<span className="text-amber-700 dark:text-amber-300">{zipReport.skipped} skipped</span>
                {" · "}<span className="text-red-700 dark:text-red-300">{zipReport.errors} errors</span>
              </div>
              <button className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700" onClick={()=>setZipReport(null)} data-testid="admin-upload-zip-report-close">Dismiss</button>
            </div>
            <ul className="text-xs divide-y divide-slate-200 max-h-56 overflow-auto">
              {zipReport.results.map((r, i) => (
                <li key={i} className="py-1.5 flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${r.status === "created" ? "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800" : r.status === "skipped" ? "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300" : "bg-red-100 text-red-800"}`}>{r.status}</span>
                  <span className="text-slate-600 dark:text-slate-300 truncate flex-1">{r.file}</span>
                  {r.title && <span className="text-slate-500 dark:text-slate-400 truncate">→ {r.title}</span>}
                  {r.reason && <span className="text-slate-400 dark:text-slate-500 italic truncate">({r.reason})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(file || previewing || preview) && (
          <div data-testid="admin-upload-preview"
               className="mt-4 border border-sky-200 dark:border-sky-900 rounded-md p-4 bg-gradient-to-br from-sky-50 dark:from-sky-950/40 to-white dark:to-slate-900">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-sky-800 dark:text-sky-300 font-bold">
                Extracted from .docx {previewing && <span className="text-slate-400 dark:text-slate-500 normal-case">— parsing…</span>}
              </div>
              <button type="button"
                      className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700"
                      onClick={() => { setFile(null); setPreview(null); const el=document.getElementById('upload-file-input'); if(el) el.value=''; }}
                      data-testid="admin-upload-preview-clear">
                Clear
              </button>
            </div>
            {preview?.__error ? (
              <p className="text-sm text-red-700 dark:text-red-300">{preview.__error}</p>
            ) : preview ? (
              <div className="grid md:grid-cols-[minmax(0,180px)_minmax(0,1fr)] gap-4">
                {preview.header_image_preview && (
                  <img data-testid="admin-upload-preview-header-img"
                       src={preview.header_image_preview}
                       alt={preview.header_image_alt || ""}
                       className="w-full h-auto rounded border border-slate-200 dark:border-slate-800 object-cover"
                       style={{ maxHeight: 220 }}/>
                )}
                <div className={preview.header_image_preview ? "" : "md:col-span-2"}>
                  <div data-testid="admin-upload-preview-title" className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight">
                    {preview.title || "Untitled"}
                  </div>
                  {preview.subtitle && (
                    <div data-testid="admin-upload-preview-subtitle" className="mt-1 text-sm italic text-slate-600 dark:text-slate-300">
                      {preview.subtitle}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {preview.author_name && (
                      <div><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">Author</span><br/>
                        <span data-testid="admin-upload-preview-author" className="text-slate-800 dark:text-slate-200">{preview.author_name}</span>
                        {preview.suggested_author_id && (
                          <span data-testid="admin-upload-preview-author-matched"
                                title="Auto-linked to an existing Authors record"
                                className="ml-2 text-[10px] uppercase tracking-widest bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 px-1.5 py-0.5 rounded normal-case">
                            ✓ matched
                          </span>
                        )}
                      </div>
                    )}
                    {preview.first_published && (
                      <div><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">First published</span><br/>
                        <span data-testid="admin-upload-preview-first-published" className="text-slate-800 dark:text-slate-200">{preview.first_published}</span>
                      </div>
                    )}
                    {preview.last_updated && (
                      <div><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">Last updated</span><br/>
                        <span data-testid="admin-upload-preview-last-updated" className="text-slate-800 dark:text-slate-200">{preview.last_updated}</span>
                      </div>
                    )}
                    {preview.meta_slug && (
                      <div><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">Slug</span><br/>
                        <span className="text-slate-800 dark:text-slate-200 font-mono">{preview.proposed_slug || preview.meta_slug}</span>
                        {preview.slug_conflict && (
                          <span data-testid="admin-upload-preview-slug-conflict"
                                className="ml-2 text-[10px] uppercase tracking-widest bg-red-100 text-red-800 px-1.5 py-0.5 rounded normal-case">
                            ⚠ already exists
                          </span>
                        )}
                      </div>
                    )}
                    {preview.meta_blog_id && (
                      <div><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">Blog ID</span><br/>
                        <span className="text-slate-800 dark:text-slate-200 font-mono">{preview.proposed_blog_id || preview.meta_blog_id}</span>
                        {preview.blog_id_conflict && (
                          <span data-testid="admin-upload-preview-blog-id-conflict"
                                className="ml-2 text-[10px] uppercase tracking-widest bg-red-100 text-red-800 px-1.5 py-0.5 rounded normal-case">
                            ⚠ already exists
                          </span>
                        )}
                      </div>
                    )}
                    {(preview.primary_module || (preview.secondary_modules || []).length > 0) && (
                      <div className="col-span-2"><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">Modules</span><br/>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {preview.primary_module && (
                            <span className="text-[10px] uppercase tracking-widest bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 px-2 py-0.5 rounded font-mono">
                              {preview.primary_module}
                            </span>
                          )}
                          {(preview.secondary_modules || []).map((m, i) => (
                            <span key={i} className="text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded font-mono">{m}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {preview.seo_description && (
                      <div className="col-span-2"><span className="uppercase tracking-widest text-slate-400 dark:text-slate-500">SEO description</span><br/>
                        <span className="text-slate-700 dark:text-slate-300">{preview.seo_description}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest">
                    <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded">{preview.section_count} section{preview.section_count === 1 ? "" : "s"}</span>
                    {preview.has_summary_321 && <span className="bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 px-2 py-0.5 rounded">3-2-1 summary</span>}
                    {preview.bibliography_count > 0 && <span className="bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded">{preview.bibliography_count} refs</span>}
                    {preview.header_image_filename && <span className="bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 px-2 py-0.5 rounded">Header img</span>}
                  </div>
                </div>
              </div>
            ) : previewing ? (
              <div className="text-xs italic text-slate-500 dark:text-slate-400">Parsing {file?.name}…</div>
            ) : null}
          </div>
        )}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 flex items-center justify-between">
            <span>All blogs ({filteredBlogs.length}{blogFilter && filteredBlogs.length !== blogs.length ? ` of ${blogs.length}` : ""})</span>
            <div className="flex items-center gap-3">
              {blogs.length > 0 && (
                <a data-testid="admin-blogs-export-all"
                   href={`${API}/admin/blogs/export-all.zip`}
                   title="Download every blog as a zipped folder of .docx files"
                   className="text-slate-500 dark:text-slate-400 hover:text-sky-700 inline-flex items-center gap-1 tracking-widest">
                  <Download className="w-3.5 h-3.5"/>Download all (.zip)
                </a>
              )}
            </div>
          </div>
          {blogs.length > 0 && (
            <ListFilter
              value={blogFilter}
              onChange={setBlogFilter}
              placeholder="Filter blogs by title or ID…"
              testId="admin-blogs-filter"
              className="mb-2"
            />
          )}
          <RemoveTestBlogsBar onDeleted={refresh}/>
          <ul className="divide-y divide-slate-200 border-y border-slate-200 dark:border-slate-800 text-sm max-h-96 overflow-auto">
            {filteredBlogs.map((b) => {
              const today = new Date().toISOString().slice(0,10);
              const isDraft = !b.release_date;
              const isScheduled = b.release_date && b.release_date > today;
              return (
                <li key={b.blog_id} data-testid={`admin-blog-row-${b.blog_id}`}
                    className="py-2 flex items-center justify-between gap-2 bg-white dark:bg-slate-900">
                  <div className="min-w-0 flex-1">
                    <div className="truncate flex items-center gap-2">
                      <span>{b.title}</span>
                      {b.is_test && (
                        <span data-testid={`admin-blog-badge-test-${b.blog_id}`}
                              className="text-[10px] uppercase tracking-widest bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded"
                              title="Marked as a test blog — excluded from public analytics">
                          Test
                        </span>
                      )}
                      {isDraft && (
                        <span data-testid={`admin-blog-badge-draft-${b.blog_id}`} className="text-[10px] uppercase tracking-widest bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded">Draft</span>
                      )}
                      {isScheduled && (
                        <span data-testid={`admin-blog-badge-scheduled-${b.blog_id}`} className="text-[10px] uppercase tracking-widest bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded whitespace-nowrap">Scheduled · {b.release_date}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 flex gap-3">
                      <span className="font-mono">{b.blog_id}</span>
                      <span>views: {b.view_count || 0}</span>
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 select-none"
                         title="Flag this blog as a test blog (excluded from analytics; swept by Remove test blogs)">
                    <input
                      type="checkbox"
                      data-testid={`admin-blog-test-flag-${b.blog_id}`}
                      checked={!!b.is_test}
                      onChange={async (e) => {
                        try {
                          await api.patch(`/blogs/${b.blog_id}/test-flag`, { is_test: e.target.checked });
                          toast.success(e.target.checked ? `${b.title} marked as test` : `${b.title} unmarked`);
                          refresh();
                        } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update test flag"); }
                      }}
                    />
                    Test
                  </label>
                  <a data-testid={`admin-blog-download-${b.blog_id}`}
                     href={`${API}/admin/blogs/${b.blog_id}/export.docx`}
                     title="Download as Word (.docx)"
                     className="text-slate-500 dark:text-slate-400 hover:text-sky-700 inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                    <Download className="w-3.5 h-3.5"/>.docx
                  </a>
                  {(isDraft || isScheduled) && (
                    <button data-testid={`admin-blog-publish-now-${b.blog_id}`}
                            onClick={()=>publishNow(b)}
                            title={isScheduled ? `Publish today instead of ${b.release_date}` : "Publish this draft today"}
                            className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                      <Zap className="w-3.5 h-3.5"/>Publish now
                    </button>
                  )}
                  {!isDraft && !isScheduled && (
                    <button data-testid={`admin-blog-unpublish-${b.blog_id}`}
                            onClick={()=>unpublish(b)}
                            title="Return this live blog to Draft (hidden from readers)"
                            className="text-amber-700 dark:text-amber-300 hover:text-amber-900 inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                      <EyeOff className="w-3.5 h-3.5"/>Unpublish
                    </button>
                  )}
                  <button data-testid={`admin-blog-edit-${b.blog_id}`} onClick={()=>setEditingId(b.blog_id)} className="text-sky-700 dark:text-sky-300 hover:text-sky-900 inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                    <Pencil className="w-3.5 h-3.5"/>Edit
                  </button>
                  <button data-testid={`admin-blog-delete-${b.blog_id}`} onClick={()=>del(b)} title="Delete (with Undo)" className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4"/></button>
                </li>
              );
            })}
            {filteredBlogs.length === 0 && (
              <li className="py-6 text-slate-400 dark:text-slate-500 text-center">
                {blogs.length === 0 ? "No blogs yet." : `No blogs match "${blogFilter}".`}
              </li>
            )}
          </ul>
        </div>

        {editingId && (
          <BlogEditor blogId={editingId} open={!!editingId}
            onOpenChange={(v)=>!v && setEditingId(null)} onSaved={refresh}/>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact bar rendered above the blog list. Mirrors the test-users flow —
 * click loads a preview of blogs classified as test, confirm deletes them
 * with a full cascade (blog + views + engagements + comments + bookmarks +
 * reads). Hidden entirely when no test blogs exist.
 */
function RemoveTestBlogsBar({ onDeleted }) {
  const [preview, setPreview] = useState(null); // {count, blogs}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/admin/test-blogs");
        if (!cancelled) setCount(r.data?.count || 0);
      } catch (_) { /* silent */ }
    };
    load();
    return () => { cancelled = true; };
  }, [onDeleted]);

  if (count === 0) return null;

  const openConfirm = async () => {
    try {
      const r = await api.get("/admin/test-blogs");
      setPreview(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load test-blog preview");
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/admin/test-blogs");
      const n = r.data?.deleted_blogs || 0;
      toast.success(n ? `Removed ${n} test blog${n === 1 ? "" : "s"} and cascaded data.` : "No test blogs matched.");
      setOpen(false);
      setCount(0);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove test blogs");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="remove-test-blogs-bar" className="mb-2 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20">
      <div className="text-xs text-red-800 dark:text-red-300 inline-flex items-center gap-2">
        <FlaskConical className="w-3.5 h-3.5"/>
        {count} test blog{count === 1 ? "" : "s"} are excluded from public analytics.
      </div>
      <Button
        data-testid="remove-test-blogs-btn"
        onClick={openConfirm}
        variant="outline"
        size="sm"
        className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30"
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5"/>
        Remove {count} test blog{count === 1 ? "" : "s"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="remove-test-blogs-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove test blogs?</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              This deletes {preview?.count ?? 0} blog{preview?.count === 1 ? "" : "s"} plus every view, engagement, comment, bookmark, and read tied to them. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {preview?.blogs?.length > 0 && (
            <div className="max-h-48 overflow-y-auto text-sm border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-900 p-2"
                 data-testid="remove-test-blogs-preview-list">
              <ul className="space-y-1">
                {preview.blogs.map(b => (
                  <li key={b.blog_id} className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300">
                    <span className="truncate">{b.title}</span>
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500">{b.slug}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                    className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="remove-test-blogs-confirm"
              onClick={doDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              Yes, remove {preview?.count ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

